import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import Badge from 'components/Badge'
import ArrowRight from 'components/Icons/ArrowRight'
import {
  INLINE_LINK_OVERRIDES,
  LINK_ARROW_GAP,
} from 'constants/inlineLinkButton'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { OloWebhookConnectionState } from 'utilities/oloWebhookStatus'

const t = buildTranslate('live_updates.card.signal.olo_webhook')

// The two states that keep the "Use the Olo event" option unselectable. A
// `connected` merchant gets no panel at all, so passing it here is a type
// error rather than a runtime fallback.
export type OloWebhookPendingState = Exclude<
  OloWebhookConnectionState,
  'connected'
>

// TODO: drop this in favour of a shared token. Duplicates constants/
// palette.ts's `grey05` (now `#F8F9FA`, matching `merchantTheme.colors.grey05`
// in src/theme.ts) — kept as a local literal here rather than switching to
// the palette token, since swapping every grey05 consumer over is a separate,
// repo-wide cleanup out of scope for this design fix.
export const GREY_05 = '#F8F9FA'

// Badge pill treatment per state (Figma 1247:20213 and siblings).
const COLORS: Record<
  OloWebhookPendingState,
  { background: string; text: string }
> = {
  not_connected: { background: palette.grey20, text: palette.grey70 },
  waiting: { background: palette.belizeHole10, text: palette.belizeHole },
}

// Fill for the attached panel itself. `waiting` would take a belizeHole tint one
// step below its badge, but the palette stops at belizeHole10 — so it reuses the
// neutral grey fill, which the blue pill still reads clearly against.
const PANEL_FILL: Record<OloWebhookPendingState, string> = {
  not_connected: GREY_05,
  waiting: GREY_05,
}

const BADGE_KEY: Record<OloWebhookPendingState, string> = {
  not_connected: 'not_connected_badge',
  waiting: 'waiting_badge',
}

const NOTE_KEY: Record<OloWebhookPendingState, string> = {
  not_connected: 'not_connected_note',
  waiting: 'waiting_note',
}

const LINK_KEY: Record<OloWebhookPendingState, string> = {
  not_connected: 'setup_link',
  waiting: 'review_link',
}

// Panel geometry, measured off Figma 1247:20209: content inset 16px from the
// left edge, 12px from the top, with 4px between the pill, the note and the
// link. No radius or border of its own — the wrapping card in SignalSelector
// owns both, so this reads as a footer attached to the card rather than a
// detached box.
const PANEL_PADDING = '12px 16px'
const PANEL_GAP = '4px'

export interface OloWebhookConnectionStatusProps {
  connectionState: OloWebhookPendingState
  onManageClick: () => void
}

// The footer panel attached beneath the "Use the Olo event" card while that
// option is still unselectable — keyed off GET olo_webhook_credentials + GET
// order_status_webhook_receipts.
//
// Both states render the same shape (pill + note + arrowed link) over a neutral
// fill; SignalSelector supplies the shared border and radius. Not connected
// offers "Set up"; waiting offers "Review setup", since the secret is saved and
// the remaining work is confirming the webhook was published in Olo.
//
// A connected merchant sees nothing here: the page-level OloWebhookBanner is the
// single source of truth for connection health, and it has nothing actionable to
// say while the connection is healthy.
const OloWebhookConnectionStatus = ({
  connectionState,
  onManageClick,
}: OloWebhookConnectionStatusProps) => {
  const [css] = useStyletron()
  const colors = COLORS[connectionState]

  const badge = (
    <Badge
      className={css({
        textTransform: 'uppercase',
        backgroundColor: colors.background,
        color: colors.text,
        alignSelf: 'flex-start',
      })}
      value={t(BADGE_KEY[connectionState])}
    />
  )

  const link = (
    <Button
      kind="minimal"
      onClick={onManageClick}
      overrides={INLINE_LINK_OVERRIDES}
      className={css({ alignSelf: 'flex-start' })}
    >
      {t(LINK_KEY[connectionState])}
      {/* Decorative: the label already says what the link does, so the glyph
          stays out of the accessible name. From the icon library rather than a
          literal "→" — raw Unicode in JSX is not allowed. */}
      <span
        aria-hidden="true"
        className={css({
          display: 'inline-flex',
          alignItems: 'center',
          marginLeft: LINK_ARROW_GAP,
        })}
      >
        <ArrowRight />
      </span>
    </Button>
  )

  return (
    <div
      data-testid="lu-olo-webhook-status"
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: PANEL_GAP,
        padding: PANEL_PADDING,
        backgroundColor: PANEL_FILL[connectionState],
      })}
    >
      {badge}
      <Text.BodyText4 color="grey70">
        {t(NOTE_KEY[connectionState])}
      </Text.BodyText4>
      {link}
    </div>
  )
}

export default OloWebhookConnectionStatus
